<script setup lang="ts">
import type { AftersalesStageKey } from './display';

import type { InboundOrderListVo } from '#/api/inbound-order/model';
import type { TiktokAftersalesItem } from '#/api/tiktok-aftersales';

import { computed, onMounted, onUnmounted, ref } from 'vue';

import dayjs from 'dayjs';
import {
  ElAlert,
  ElButton,
  ElCard,
  ElDatePicker,
  ElDescriptions,
  ElDescriptionsItem,
  ElDialog,
  ElForm,
  ElFormItem,
  ElImage,
  ElInput,
  ElMessage,
  ElMessageBox,
  ElOption,
  ElPagination,
  ElSelect,
  ElSpace,
  ElTable,
  ElTableColumn,
  ElTabPane,
  ElTabs,
  ElTag,
  ElTooltip,
} from 'element-plus';

import { getShopListApi } from '#/api/core/auth';
import {
  approveTiktokAftersalesApi,
  getTiktokAftersalesDecisionEligibilityApi,
  rejectTiktokAftersalesApi,
  searchTiktokAftersalesApi,
  syncTiktokAftersalesApi,
} from '#/api/tiktok-aftersales';
import InboundOrderCreateSplitDialog from '#/views/workspace/inbound-order/components/InboundOrderCreateSplitDialog.vue';
import InboundOrderDetailDialog from '#/views/workspace/inbound-order/components/InboundOrderDetailDialog.vue';

import {
  aftersalesStageOptions,
  businessStatus,
  formatAmount,
  formatTimestamp,
  payoutDisplay,
  refundDisplay,
  remainingDuration,
  returnReasonDisplay,
  returnStatusLabel,
  returnTypeLabel,
} from './display';

interface ShopOption {
  label: string;
  value: number;
}

const shops = ref<ShopOption[]>([]);
const selectedShopId = ref(0);
const orderId = ref('');
const returnId = ref('');
const trackingNumber = ref('');
const returnTypes = ref<string[]>([]);
const returnStatuses = ref<string[]>([]);
const stageFilter = ref<AftersalesStageKey>('ALL');
const dateRange = ref<[Date, Date]>();
const rows = ref<TiktokAftersalesItem[]>([]);
const total = ref(0);
const pageNum = ref(1);
const pageSize = ref(20);
const loading = ref(false);
const syncLoading = ref(false);
const syncing = ref(false);
const lastSyncedAt = ref<number>();
const warnings = ref<string[]>([]);
const mutationEnabled = ref(false);
const detail = ref<TiktokAftersalesItem>();
const returnInboundTarget = ref<TiktokAftersalesItem>();
const returnInboundDetail = ref<InboundOrderListVo | null>(null);
const returnInboundDetailVisible = ref(false);
const actionLoadingId = ref('');
const rejectTarget = ref<TiktokAftersalesItem>();
const rejectReasons = ref<Array<{ label: string; value: string }>>([]);
const rejectReason = ref('');
const rejectComment = ref('');
const rejectSubmitting = ref(false);
const nowSeconds = ref(dayjs().unix());
let refreshTimer: ReturnType<typeof setInterval> | undefined;
let countdownTimer: ReturnType<typeof setInterval> | undefined;

const rejectDialogVisible = computed({
  get: () => Boolean(rejectTarget.value),
  set: (value) => {
    if (!value) rejectTarget.value = undefined;
  },
});

function productImage(item: Record<string, any>) {
  const image =
    item.sku_image || item.skuImage || item.product_image || item.image;
  return typeof image === 'string' ? image : image?.url;
}

function productTitle(item: Record<string, any>) {
  return item.product_name || item.productName || item.title || '-';
}

function productSku(item: Record<string, any>) {
  return (
    item.seller_sku || item.sellerSku || item.sku_name || item.skuId || '-'
  );
}

function warehouseName(row: TiktokAftersalesItem) {
  return row.returnWarehouse?.name || row.returnWarehouse?.warehouseName || '-';
}

function warehouseAddress(row: TiktokAftersalesItem) {
  const address = row.returnWarehouse?.address || row.returnWarehouse;
  if (!address) return '-';
  const official = String(address.fullAddress || '').trim();
  const normalizedOfficial = normalizeAddress(official);
  const supplemental = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3,
    address.addressLine4,
    address.town,
    address.district,
    address.city,
    address.state,
    address.postalCode,
    address.region,
  ].filter((value) => {
    const normalized = normalizeAddress(value);
    return normalized && !normalizedOfficial.includes(normalized);
  });
  return [official, ...supplemental].filter(Boolean).join(' ') || '-';
}

function normalizeAddress(value: unknown) {
  return String(value || '')
    .toLowerCase()
    .replaceAll(/[^\p{L}\p{N}]/gu, '');
}

function returnReason(row: TiktokAftersalesItem) {
  return returnReasonDisplay(row);
}

function sellerTypeLabel(type?: string) {
  if (type === 'LOCAL') return '本土店（LOCAL）';
  if (type === 'POP') return '跨境店（POP）';
  return type || '-';
}

function remainingTime(deadline?: number) {
  return remainingDuration(deadline, nowSeconds.value);
}

async function changeStage(value: number | string) {
  const option = aftersalesStageOptions.find((item) => item.key === value);
  returnStatuses.value = option ? [...option.statuses] : [];
  await query();
}

function decisions(row: TiktokAftersalesItem) {
  if (row.returnStatus === 'BUYER_SHIPPED_ITEM') {
    return {
      approve: 'APPROVE_RECEIVED_PACKAGE',
      approveLabel: '确认收货',
      reject: 'REJECT_RECEIVED_PACKAGE',
      rejectLabel: '拒绝收货',
    };
  }
  if (
    ![
      'REPLACEMENT_REQUEST_PENDING',
      'RETURN_OR_REFUND_REQUEST_PENDING',
    ].includes(row.returnStatus || '')
  ) {
    return undefined;
  }
  if (row.returnType === 'REFUND') {
    return {
      approve: 'APPROVE_REFUND',
      approveLabel: '同意退款',
      reject: 'REJECT_REFUND',
      rejectLabel: '拒绝退款',
    };
  }
  if (row.returnType === 'REPLACEMENT') {
    return {
      approve: 'APPROVE_REPLACEMENT',
      approveLabel: '同意换货',
      reject: 'REJECT_REPLACEMENT',
      rejectLabel: '拒绝换货',
    };
  }
  return {
    approve: 'APPROVE_RETURN',
    approveLabel: '同意退货',
    reject: 'REJECT_RETURN',
    rejectLabel: '拒绝退货',
  };
}

function eligibilityItems(data: Record<string, any> = {}) {
  return (
    data.decisions || data.decision_eligibilities || data.eligibilities || []
  );
}

async function checkEligibility(row: TiktokAftersalesItem, decision: string) {
  const data = await getTiktokAftersalesDecisionEligibilityApi({
    decision,
    returnId: row.returnId || '',
    shopId: row.shopId || 0,
  });
  const current = eligibilityItems(data).find(
    (item: Record<string, any>) =>
      (item.decision || item.check_decision) === decision,
  );
  if (current && (current.eligible === false || current.available === false)) {
    throw new Error(
      current.reason || current.message || '当前状态不允许该操作',
    );
  }
  return current || data;
}

async function approveRow(row: TiktokAftersalesItem) {
  const action = decisions(row);
  if (!action || !row.returnId || !row.shopId) return;
  try {
    await ElMessageBox.confirm(
      `确认对店铺「${row.shopName || '-'}」的售后单 ${row.returnId} 执行“${action.approveLabel}”？`,
      '确认 TikTok 售后处理',
      { type: 'warning' },
    );
  } catch {
    return;
  }
  actionLoadingId.value = row.returnId;
  try {
    await checkEligibility(row, action.approve);
    await approveTiktokAftersalesApi({
      decision: action.approve,
      returnId: row.returnId,
      shopId: row.shopId,
    });
    ElMessage.success('已提交 TikTok，正在刷新售后状态');
    await loadRows();
  } finally {
    actionLoadingId.value = '';
  }
}

function normalizeRejectReasons(value: Record<string, any> = {}) {
  const values =
    value.available_reject_reasons ||
    value.reject_reasons ||
    value.reasons ||
    [];
  return values
    .map((item: Record<string, any> | string) => {
      if (typeof item === 'string') return { label: item, value: item };
      const reasonValue =
        item.reason || item.code || item.value || item.id || item.name;
      return reasonValue
        ? {
            label:
              item.reason_text ||
              item.display_name ||
              item.label ||
              item.text ||
              item.name ||
              reasonValue,
            value: reasonValue,
          }
        : undefined;
    })
    .filter(Boolean) as Array<{ label: string; value: string }>;
}

async function openReject(row: TiktokAftersalesItem) {
  const action = decisions(row);
  if (!action || !row.returnId) return;
  actionLoadingId.value = row.returnId;
  try {
    const eligibility = await checkEligibility(row, action.reject);
    rejectReasons.value = normalizeRejectReasons(eligibility);
    if (rejectReasons.value.length === 0) {
      ElMessage.warning(
        'TikTok 未返回当前可用的拒绝原因，为避免误操作未开放提交',
      );
      return;
    }
    rejectReason.value = '';
    rejectComment.value = '';
    rejectTarget.value = row;
  } finally {
    actionLoadingId.value = '';
  }
}

async function submitReject() {
  const row = rejectTarget.value;
  const action = row && decisions(row);
  if (!row || !action || !row.returnId || !row.shopId || !rejectReason.value)
    return;
  try {
    await ElMessageBox.confirm(
      `确认拒绝店铺「${row.shopName || '-'}」的售后单 ${row.returnId}？`,
      '确认拒绝',
      { confirmButtonText: '确认拒绝', type: 'warning' },
    );
  } catch {
    return;
  }
  rejectSubmitting.value = true;
  try {
    await rejectTiktokAftersalesApi({
      comment: rejectComment.value || undefined,
      decision: action.reject,
      rejectReason: rejectReason.value,
      returnId: row.returnId,
      shopId: row.shopId,
    });
    rejectTarget.value = undefined;
    ElMessage.success('已提交 TikTok，正在刷新售后状态');
    await loadRows();
  } finally {
    rejectSubmitting.value = false;
  }
}

async function loadShops() {
  const result = await getShopListApi({
    authStatus: 1,
    pageNum: 1,
    pageSize: 500,
  });
  shops.value = (result.rows || []).map((shop: any) => ({
    label: shop.shopAlias || shop.tiktokShopName || `店铺 ${shop.shopId}`,
    value: shop.shopId,
  }));
}

async function loadRows() {
  loading.value = true;
  try {
    const result = await searchTiktokAftersalesApi({
      createTimeGe: dateRange.value?.[0]
        ? dayjs(dateRange.value[0]).startOf('day').unix()
        : undefined,
      createTimeLt: dateRange.value?.[1]
        ? dayjs(dateRange.value[1]).add(1, 'day').startOf('day').unix()
        : undefined,
      orderId: orderId.value || undefined,
      pageNum: pageNum.value,
      pageSize: pageSize.value,
      returnId: returnId.value || undefined,
      returnStatuses: returnStatuses.value,
      returnTypes: returnTypes.value,
      shopId: selectedShopId.value || undefined,
      trackingNumber: trackingNumber.value || undefined,
    });
    rows.value = result.rows || [];
    total.value = result.total || 0;
    warnings.value = result.warnings || [];
    mutationEnabled.value = result.mutationEnabled === true;
    syncing.value = result.syncing === true;
    lastSyncedAt.value = result.lastSyncedAt;
  } finally {
    loading.value = false;
  }
}

async function query() {
  pageNum.value = 1;
  await loadRows();
}

async function reset() {
  selectedShopId.value = 0;
  orderId.value = '';
  returnId.value = '';
  trackingNumber.value = '';
  returnTypes.value = [];
  returnStatuses.value = [];
  stageFilter.value = 'ALL';
  dateRange.value = undefined;
  await query();
}

async function changePage(value: number) {
  pageNum.value = value;
  await loadRows();
}

async function changePageSize(value: number) {
  pageSize.value = value;
  pageNum.value = 1;
  await loadRows();
}

async function syncRows() {
  syncLoading.value = true;
  try {
    await syncTiktokAftersalesApi({
      shopId: selectedShopId.value || undefined,
    });
    syncing.value = true;
    ElMessage.success('售后数据同步已启动，稍后刷新即可查看');
    setTimeout(loadRows, 1500);
  } finally {
    syncLoading.value = false;
  }
}

function openReturnInbound(row: TiktokAftersalesItem) {
  if (!row.returnInboundEligible) {
    ElMessage.warning(
      row.returnInboundDisabledReason || '当前售后不能创建退货入库',
    );
    return;
  }
  returnInboundTarget.value = row;
}

function openExistingReturnInbound(row: TiktokAftersalesItem) {
  if (!row.returnInboundOrderId || !row.returnInboundOrderNo) return;
  returnInboundDetail.value = {
    id: row.returnInboundOrderId,
    inboundOrderNo: row.returnInboundOrderNo,
    inboundStatus: (row.returnInboundStatus ||
      'TO_INBOUND') as InboundOrderListVo['inboundStatus'],
    inboundTrackingNo: row.returnTrackingNumber || '',
  };
  returnInboundDetailVisible.value = true;
}

function closeReturnInbound(value: boolean) {
  if (!value) returnInboundTarget.value = undefined;
}

onMounted(async () => {
  await loadShops();
  await loadRows();
  refreshTimer = setInterval(
    () => {
      if (!document.hidden && !loading.value) loadRows();
    },
    5 * 60 * 1000,
  );
  countdownTimer = setInterval(() => {
    nowSeconds.value = dayjs().unix();
  }, 1000);
});

onUnmounted(() => {
  if (refreshTimer) clearInterval(refreshTimer);
  if (countdownTimer) clearInterval(countdownTimer);
});
</script>

<template>
  <div class="aftersales-page">
    <ElCard shadow="never">
      <template #header>
        <div class="header-row">
          <div>
            <h2>TikTok 售后管理</h2>
            <p>默认展示当前客户全部已授权店铺的售后数据。</p>
          </div>
          <ElSpace>
            <span v-if="lastSyncedAt" class="sync-time">
              最近同步：{{ formatTimestamp(lastSyncedAt) }}
            </span>
            <ElTag v-if="syncing" type="warning">同步中</ElTag>
            <ElButton :loading="syncLoading" @click="syncRows">
              同步售后
            </ElButton>
            <ElTag :type="mutationEnabled ? 'warning' : 'info'">
              {{ mutationEnabled ? '可处理' : '只读模式' }}
            </ElTag>
          </ElSpace>
        </div>
      </template>

      <ElAlert
        v-for="message in warnings"
        :key="message"
        class="warning"
        :closable="false"
        show-icon
        type="warning"
        :title="message"
      />

      <ElAlert
        class="operation-tip"
        :closable="false"
        show-icon
        type="info"
        :title="
          mutationEnabled
            ? '待处理售后可执行同意或拒绝；操作前会二次确认，退货入库仍需用户确认后提交。'
            : '当前为只读模式，暂不提交 TikTok 售后决策。'
        "
      />

      <ElTabs
        v-model="stageFilter"
        class="status-tabs"
        @tab-change="changeStage"
      >
        <ElTabPane
          v-for="option in aftersalesStageOptions"
          :key="option.key"
          :label="option.label"
          :name="option.key"
        />
      </ElTabs>

      <div class="filters">
        <ElSelect
          v-model="selectedShopId"
          clearable
          filterable
          placeholder="全部店铺"
        >
          <ElOption label="全部店铺" :value="0" />
          <ElOption
            v-for="shop in shops"
            :key="shop.value"
            :label="shop.label"
            :value="shop.value"
          />
        </ElSelect>
        <ElInput v-model="orderId" clearable placeholder="TikTok 订单号" />
        <ElInput v-model="returnId" clearable placeholder="售后/退货单号" />
        <ElInput
          v-model="trackingNumber"
          clearable
          placeholder="退回物流单号"
        />
        <ElSelect
          v-model="returnTypes"
          clearable
          collapse-tags
          multiple
          placeholder="售后类型"
        >
          <ElOption label="仅退款" value="REFUND" />
          <ElOption label="退货退款" value="RETURN_AND_REFUND" />
          <ElOption label="换货" value="REPLACEMENT" />
        </ElSelect>
        <ElSelect
          v-model="returnStatuses"
          clearable
          collapse-tags
          multiple
          placeholder="售后状态"
        >
          <ElOption label="待处理" value="RETURN_OR_REFUND_REQUEST_PENDING" />
          <ElOption label="待买家退回" value="AWAITING_BUYER_SHIP" />
          <ElOption label="买家已退回" value="BUYER_SHIPPED_ITEM" />
          <ElOption label="已完成" value="RETURN_OR_REFUND_REQUEST_SUCCESS" />
          <ElOption label="已拒绝" value="RETURN_OR_REFUND_REQUEST_REJECT" />
        </ElSelect>
        <ElDatePicker
          v-model="dateRange"
          end-placeholder="申请结束日"
          start-placeholder="申请开始日"
          type="daterange"
        />
        <div class="filter-actions">
          <ElButton type="primary" :loading="loading" @click="query">
            查询
          </ElButton>
          <ElButton @click="reset">重置</ElButton>
        </div>
      </div>

      <div class="result-summary">共 {{ total }} 条售后记录</div>

      <ElTable v-loading="loading" border :data="rows" row-key="returnId">
        <ElTableColumn label="店铺" min-width="180">
          <template #default="{ row }">
            <div>{{ row.shopName || '-' }}</div>
            <small>{{ sellerTypeLabel(row.sellerType) }}</small>
          </template>
        </ElTableColumn>
        <ElTableColumn label="单号" min-width="205">
          <template #default="{ row }">
            <div>售后：{{ row.returnId || '-' }}</div>
            <div>订单：{{ row.orderId || '-' }}</div>
          </template>
        </ElTableColumn>
        <ElTableColumn label="商品" min-width="260">
          <template #default="{ row }">
            <div
              v-for="(item, index) in row.lineItems || []"
              :key="item.id || item.sku_id || index"
              class="product-cell"
            >
              <ElImage
                v-if="productImage(item)"
                class="product-image"
                fit="cover"
                :preview-src-list="[productImage(item)]"
                preview-teleported
                :src="productImage(item)"
              />
              <div>
                <div>{{ productTitle(item) }}</div>
                <small>SKU：{{ productSku(item) }}</small>
                <small>
                  数量：{{ item.quantity || item.return_quantity || 1 }}
                </small>
              </div>
            </div>
            <span v-if="!row.lineItems?.length">-</span>
          </template>
        </ElTableColumn>
        <ElTableColumn label="类型/状态" min-width="190">
          <template #default="{ row }">
            <div>{{ returnTypeLabel(row.returnType) }}</div>
            <ElTag size="small" :type="businessStatus(row).type">
              {{ businessStatus(row).label }}
            </ElTag>
            <small :title="row.returnStatus">
              {{ returnStatusLabel(row.returnStatus) }}
            </small>
          </template>
        </ElTableColumn>
        <ElTableColumn label="金额" min-width="165">
          <template #default="{ row }">
            <div>实付：{{ formatAmount(row.buyerPaidAmount) }}</div>
            <div>
              {{ payoutDisplay(row).label }}：{{ payoutDisplay(row).amount }}
            </div>
            <div>退款：{{ refundDisplay(row) }}</div>
          </template>
        </ElTableColumn>
        <ElTableColumn label="售后原因" min-width="180">
          <template #default="{ row }">{{ returnReason(row) }}</template>
        </ElTableColumn>
        <ElTableColumn label="退回物流" min-width="180">
          <template #default="{ row }">
            <div>{{ row.returnProviderName || '-' }}</div>
            <div>{{ row.returnTrackingNumber || '-' }}</div>
          </template>
        </ElTableColumn>
        <ElTableColumn label="退货仓" min-width="240">
          <template #default="{ row }">
            <div>{{ warehouseName(row) }}</div>
            <small>{{ warehouseAddress(row) }}</small>
          </template>
        </ElTableColumn>
        <ElTableColumn label="时间" min-width="215">
          <template #default="{ row }">
            <div>下单：{{ formatTimestamp(row.orderedTime) }}</div>
            <div>付款：{{ formatTimestamp(row.paidTime) }}</div>
            <div>发货：{{ formatTimestamp(row.shippedTime) }}</div>
            <div>
              售后：{{ formatTimestamp(row.aftersaleTime || row.createTime) }}
            </div>
            <ElTag
              v-if="row.processingDeadline"
              size="small"
              :type="
                remainingTime(row.processingDeadline) === '已超时'
                  ? 'danger'
                  : 'warning'
              "
            >
              处理倒计时：{{ remainingTime(row.processingDeadline) }}
            </ElTag>
          </template>
        </ElTableColumn>
        <ElTableColumn fixed="right" label="操作" width="320">
          <template #default="{ row }">
            <ElButton link type="primary" @click="detail = row">详情</ElButton>
            <ElButton
              v-if="row.returnInboundOrderId"
              link
              type="primary"
              @click="openExistingReturnInbound(row)"
            >
              查看入库单（{{ row.returnInboundStatusLabel || '已创建' }}）
            </ElButton>
            <ElTooltip
              v-else
              :disabled="row.returnInboundEligible"
              :content="row.returnInboundDisabledReason || '当前不可创建'"
            >
              <span>
                <ElButton
                  link
                  type="primary"
                  :disabled="!row.returnInboundEligible"
                  @click="openReturnInbound(row)"
                >
                  创建退货入库
                </ElButton>
              </span>
            </ElTooltip>
            <template v-if="mutationEnabled && decisions(row)">
              <ElButton
                link
                type="success"
                :loading="actionLoadingId === row.returnId"
                @click="approveRow(row)"
              >
                {{ decisions(row)?.approveLabel }}
              </ElButton>
              <ElButton
                link
                type="danger"
                :loading="actionLoadingId === row.returnId"
                @click="openReject(row)"
              >
                {{ decisions(row)?.rejectLabel }}
              </ElButton>
            </template>
          </template>
        </ElTableColumn>
      </ElTable>

      <ElPagination
        class="pagination"
        :current-page="pageNum"
        layout="total, sizes, prev, pager, next, jumper"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="changePage"
        @size-change="changePageSize"
      />
    </ElCard>

    <InboundOrderCreateSplitDialog
      mode="return"
      :model-value="Boolean(returnInboundTarget)"
      :preset-authorized-order="
        returnInboundTarget
          ? { platformOrderNo: returnInboundTarget.orderId }
          : undefined
      "
      :preset-purchase-order-ids="
        returnInboundTarget?.linkedShippedPurchaseOrderIds || []
      "
      :preset-return-aftersale="returnInboundTarget"
      @refresh="loadRows"
      @update:model-value="closeReturnInbound"
    />

    <InboundOrderDetailDialog
      v-model="returnInboundDetailVisible"
      :row="returnInboundDetail"
    />

    <ElDialog
      :model-value="Boolean(detail)"
      title="TikTok 售后详情"
      width="820px"
      @close="detail = undefined"
    >
      <ElDescriptions v-if="detail" :column="2" border>
        <ElDescriptionsItem label="店铺">
          {{ detail.shopName || '-' }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="TikTok 订单号">
          {{ detail.orderId || '-' }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="售后单号">
          {{ detail.returnId || '-' }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="类型 / 状态">
          {{ returnTypeLabel(detail.returnType) }} /
          {{ returnStatusLabel(detail.returnStatus) }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="实付">
          {{ formatAmount(detail.buyerPaidAmount) }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="预估回款">
          {{ payoutDisplay(detail).amount }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="退款">
          {{ refundDisplay(detail) }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="退回物流">
          {{ detail.returnProviderName || '-' }} /
          {{ detail.returnTrackingNumber || '-' }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="售后原因" :span="2">
          {{ returnReason(detail) }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="退货仓">
          {{ warehouseName(detail) }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="邮编">
          {{ detail.returnWarehouse?.address?.postalCode || '-' }}
        </ElDescriptionsItem>
        <ElDescriptionsItem label="完整退货地址" :span="2">
          {{ warehouseAddress(detail) }}
        </ElDescriptionsItem>
      </ElDescriptions>
      <template #footer>
        <ElButton @click="detail = undefined">关闭</ElButton>
      </template>
    </ElDialog>

    <ElDialog
      v-model="rejectDialogVisible"
      title="拒绝 TikTok 售后申请"
      width="560px"
    >
      <ElAlert
        :closable="false"
        show-icon
        type="warning"
        title="拒绝原因由 TikTok 按当前售后状态实时返回，提交后不可撤销。"
      />
      <ElForm class="reject-form" label-position="top">
        <ElFormItem label="拒绝原因">
          <ElSelect v-model="rejectReason" filterable placeholder="请选择">
            <ElOption
              v-for="reason in rejectReasons"
              :key="reason.value"
              :label="reason.label"
              :value="reason.value"
            />
          </ElSelect>
        </ElFormItem>
        <ElFormItem label="补充说明">
          <ElInput v-model="rejectComment" type="textarea" :rows="3" />
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton @click="rejectTarget = undefined">取消</ElButton>
        <ElButton
          type="danger"
          :disabled="!rejectReason"
          :loading="rejectSubmitting"
          @click="submitReject"
        >
          确认拒绝
        </ElButton>
      </template>
    </ElDialog>
  </div>
</template>

<style scoped>
.aftersales-page {
  padding: 16px;
}

.header-row {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.header-row h2 {
  margin: 0;
  font-size: 20px;
}

.header-row p {
  margin: 6px 0 0;
  color: var(--el-text-color-secondary);
}

.sync-time,
.product-cell small,
.el-table small {
  color: var(--el-text-color-secondary);
}

.warning {
  margin-bottom: 12px;
}

.operation-tip {
  margin-bottom: 12px;
}

.status-tabs {
  margin-bottom: 4px;
}

.filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.filter-actions {
  display: flex;
}

.result-summary {
  margin-bottom: 10px;
  color: var(--el-text-color-secondary);
}

.product-cell {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}

.product-cell > div:last-child {
  display: flex;
  flex-direction: column;
}

.product-image {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 4px;
}

.pagination {
  justify-content: flex-end;
  margin-top: 16px;
}

.reject-form {
  margin-top: 16px;
}

.reject-form .el-select {
  width: 100%;
}

@media (max-width: 1100px) {
  .filters {
    grid-template-columns: repeat(2, minmax(190px, 1fr));
  }
}
</style>
